import type { Locale } from 'content/data';
import { useMemo } from 'react';
import i18next from 'i18next';
import resources from 'i18n/resources';

/**
 * A fresh, isolated i18next instance per locale, meant to be scoped to a page's tree via
 * I18nextProvider - NOT `.use(initReactI18next)`/registered as the app-wide default, and
 * never `.changeLanguage()`d after creation. @anywayseo/tools's own bundled i18n instance
 * hardcodes `fallbackLng: 'en'`; since it shares the same underlying i18next singleton as
 * our own default instance, calling `changeLanguage()` on either makes `lng === fallbackLng`
 * for that instance and crashes (infinite recursion in i18next's LanguageUtil). A brand new,
 * never-mutated instance avoids that code path entirely while still correctly localizing
 * useTranslation() calls inside the wrapped subtree (including the library's own components).
 */
function usePageI18n(locale: Locale) {
  return useMemo(() => {
    const instance = i18next.createInstance();
    instance.init({ lng: locale, fallbackLng: false, resources });
    return instance;
  }, [locale]);
}

export default usePageI18n;
